import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import { Badge, Button, Link, Paper, Stack, Box, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import usePolicyAcknowledgmentReport from './_data_sources/usePolicyAcknowledgmentsReport';
import { AppContext } from 'views/App/context';
import datetimeHelper from 'helpers/datetimeHelper';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { QuickSearch } from 'components/_atoms';
import { PolicyAcknowledgmentFullAuditResult } from 'views/App/Portal/Reporting/PolicyAcknowledgments/_types/policyAcknowledgements';
import Filters from './Filters';
import usePolicyAcknowledgementsReportExport from './_data_sources/usePolicyAcknowledgementsReportExport';
import { enqueueSnackbar } from 'notistack';
import { Check, Close } from '@mui/icons-material';
import { error, neutral, primary, success, warning } from '_lex/_foundations/phxColors';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';
import IncompleteAcksModal from './_components/IncompleteAcksModal';

export type PolicyAcknowledgementsParams = {
  assignedDateStart?: string | null;
  assignedDateEnd?: string | null;
  completed?: string | null;
  search?: string | null;
  groups?: string[] | null;
  manuals?: string[] | null;
  users?: string[] | null;
  status?: string | null;
};

const statusColors = {
  ARCHIVED: [neutral[50], neutral[400]],
  CANCELLED: [neutral[50], neutral[400]],
  NOT_COMPLETED: [warning[50], warning[800]],
  COMPLETED_LATE: [warning[50], warning[800]],
  COMPLETED_ON_TIME: [success[50], success[800]],
  OVERDUE: [error[50], error[800]],
  REPLACED: [neutral[50], neutral[400]],
  UNASSIGNED: [neutral[50], neutral[400]],
};

const defaultParams: PolicyAcknowledgementsParams = {
  assignedDateStart: null,
  assignedDateEnd: null,
  completed: null,
  search: null,
  groups: [],
  manuals: [],
  status: null,
  users: [],
};

const PolicyAcknowledgements: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = React.useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);
  const [results, setResults] = useState<PolicyAcknowledgmentFullAuditResult[]>([]);
  const { generate: generateExport } = usePolicyAcknowledgementsReportExport();
  const [incompleteAcksModalOpen, setIncompleteAcksModalOpen] = useState(false);

  const updateParams = useCallback(
    (updatedParams: PolicyAcknowledgementsParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const newParams = _.omitBy(
      Object.fromEntries(
        Object.entries({ ...params }).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])
      ),
      _.isEmpty
    );

    return newParams;
  }, [params]);

  const isFiltered = !_.isEmpty(queryParams);

  const reportQuery = usePolicyAcknowledgmentReport(currentOrganizationId, queryParams);

  const handleExportClick = useCallback(() => {
    generateExport(currentOrganizationId, queryParams, () => {
      enqueueSnackbar(parse(t(`reporting.policyAcknowledgments.retrievalConfirmation`, '', {})), {
        variant: 'success',
        autoHideDuration: 6000,
      });
      window.setTimeout(() => {
        queryClient.invalidateQueries('notifications_unread_count');
        queryClient.invalidateQueries('notifications');
      }, 15000);
    });
  }, [queryClient, currentOrganizationId, queryParams, t]);

  useEffect(() => {
    if (reportQuery.data && !reportQuery.isError && reportQuery.data?.pages?.length > 0) {
      const fetchedResults = reportQuery.data.pages.flatMap((page) => page.content) || [];
      setResults(fetchedResults);
    }
  }, [reportQuery.data, reportQuery.isError]);

  const columns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('reporting.policyAcknowledgments.columns.name'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{[row.firstName, row.lastName].join(' ')}</span>;
        },
      },
      email: {
        header: t('reporting.policyAcknowledgments.columns.email'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.email}</span>;
        },
      },
      manual: {
        header: t('reporting.policyAcknowledgments.columns.manual'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.manual?.name}</span>;
        },
      },
      policy: {
        header: t('reporting.policyAcknowledgments.columns.policy'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{[row.policy?.number, row.policy?.name].filter((x) => !_.isEmpty(x)).join(': ')}</span>;
        },
      },
      assignedDate: {
        header: t('reporting.policyAcknowledgments.columns.assignedDate'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDate(row.assignedDate)}</span>;
        },
      },
      complete: {
        header: t('reporting.policyAcknowledgments.columns.complete'),
        flex: 1,
        align: 'center',
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return row.complete ? <Check sx={{ color: success[800] }} /> : <Close sx={{ color: error[800] }} />;
        },
      },
      status: {
        header: t('reporting.policyAcknowledgments.columns.status'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          const statusColor = statusColors[row.status as keyof typeof statusColors] || statusColors.ARCHIVED;
          if (!row) return null;
          return (
            <Badge
              variant='inline'
              badgeContent={t(`reporting.policyAcknowledgments.status.${row.status}`)}
              color='neutral'
              sx={{
                '& .MuiBadge-badge.MuiBadge-inline': {
                  backgroundColor: statusColor[0],
                  color: statusColor[1],
                },
              }}
            />
          );
        },
      },
    }),
    [t]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('reporting.policyAcknowledgments.title'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 4, minHeight: 0 }}>
        <Paper
          elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 4,
              borderRadius: 2,
              backgroundColor: primary[50],
              border: `1px solid ${neutral[200]}`,
            }}
          >
            <Typography
              variant='body1'
              sx={{ color: neutral[900] }}
            >
              {t('reporting.policyAcknowledgments.incompleteAcks.banner.description')}
            </Typography>
            <Link
              component='button'
              variant='body1'
              onClick={() => setIncompleteAcksModalOpen(true)}
              sx={{
                color: primary[700],
                fontWeight: 600,
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('reporting.policyAcknowledgments.incompleteAcks.banner.downloadReport')}
            </Link>
          </Paper>

        <PageToolbar
          leftItems={
            <QuickSearch
              id='users_search'
              initialValue={params.search || ''}
              onChange={(val) => updateParams({ search: val?.trim() })}
              placeholder={t('reporting.policyAcknowledgments.search.placeholder')}
            />
          }
          rightItems={
            <Stack
              direction='row'
              spacing={2}
            >
              {isFiltered && (
                <Button
                  color='neutral'
                  onClick={() => clearParams()}
                  size='small'
                >
                  {t('reporting.policyAcknowledgments.actions.clearFilters')}
                </Button>
              )}
              <Filters
                key={`filters_${JSON.stringify(params)}`}
                organizationId={currentOrganizationId}
                params={params}
                updateParams={updateParams}
              />
              <Button
                size='small'
                variant='outlined'
                onClick={() => handleExportClick()}
              >
                {t('reporting.policyAcknowledgments.actions.export')}
              </Button>
            </Stack>
          }
        />
        <GridSection
          columns={columns}
          data={results}
          query={reportQuery}
          gridProps={{
            getRowId: () => uuidv4(),
          }}
        />
      </Box>

      <IncompleteAcksModal
        open={incompleteAcksModalOpen}
        onClose={() => setIncompleteAcksModalOpen(false)}
      />
    </CommonLayout>
  );
};

export default PolicyAcknowledgements;
